import { Drawer, Form } from 'antd';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';

const NO_SYMBOLS_REGEX = /^[a-zA-Z0-9\s]*$/;

export default function CustomCostcodeCategoryAddEditDrawer({
  selectedEntry,
  visible,
  toggleDrawer,
  onSave,
  onDelete,
  form,
}) {
  const categoriesMap = useSelector((state) => state.costcodes.categoriesIdMap);

  const existingNames = useMemo(() => {
    const categories = Object.values(categoriesMap || {});
    return new Set(categories.map((cat) => cat.name?.toLowerCase()));
  }, [categoriesMap]);

  const nameRules = useMemo(() => [
    { required: true, message: 'Name is required' },
    { max: 20, message: 'Name must be 20 characters or less' },
    {
      pattern: NO_SYMBOLS_REGEX,
      message: 'Name can only contain letters, numbers, and spaces',
    },
    {
      validator: (_, value) => {
        if (!value) return Promise.resolve();
        const normalizedValue = value.toLowerCase();
        const currentName = selectedEntry?.name?.toLowerCase();
        // Allow keeping the same name when editing
        if (currentName && normalizedValue === currentName) {
          return Promise.resolve();
        }
        if (existingNames.has(normalizedValue)) {
          return Promise.reject(new Error('A category with this name already exists'));
        }
        return Promise.resolve();
      },
    },
  ], [existingNames, selectedEntry]);

  return (
    <Drawer
      title={`${selectedEntry ? 'Edit' : 'Add'} Costcode Category`}
      visible={visible}
      onClose={toggleDrawer}
      width={600}
      destroyOnClose
      keyboard={false}
    >
      <Form form={form}>
        <Form.Item
          name="name"
          label="Name"
          style={{ width: '100%' }}
          rules={nameRules}
        >
          <OnTraccrTextInput placeholder="Enter Name" maxLength={20} />
        </Form.Item>
        <Form.Item name="allowTimeTracking">
          <OnTraccrCheckbox
            label="Allow Time Tracking"
            hoverNode={(
              <div style={{ width: 300 }}>
                If you check this box, costcodes in this category can be used for time tracking.
              </div>
            )}
          />
        </Form.Item>
        <Form.Item name="allowInvoiceUpload">
          <OnTraccrCheckbox
            label="Allow Invoice Upload"
            hoverNode={(
              <div style={{ width: 300 }}>
                If you check this box, costcodes in this category can be used for invoice uploads.
              </div>
            )}
          />
        </Form.Item>
        <Form.Item name="allowQuantityTracking">
          <OnTraccrCheckbox
            label="Allow Quantity Tracking"
            hoverNode={(
              <div style={{ width: 300 }}>
                If you check this box, costcodes in this category can be used for quantity tracking.
              </div>
            )}
          />
        </Form.Item>
      </Form>
      <DrawerSubmitFooter
        onClose={toggleDrawer}
        onSubmit={onSave}
        onDelete={selectedEntry ? () => onDelete(selectedEntry) : undefined}
      />
    </Drawer>
  );
}

CustomCostcodeCategoryAddEditDrawer.propTypes = {
  selectedEntry: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    allowTimeTracking: PropTypes.bool,
    allowInvoiceUpload: PropTypes.bool,
    allowQuantityTracking: PropTypes.bool,
  }),
  visible: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func,
    getFieldValue: PropTypes.func,
    validateFields: PropTypes.func,
    resetFields: PropTypes.func,
  }).isRequired,
};

CustomCostcodeCategoryAddEditDrawer.defaultProps = {
  selectedEntry: undefined,
  onDelete: undefined,
};
